import { useSondertagContext } from "../context/sondertagContext";

export default function useSondertagPDF() {
  const {
    selectedSchoolyear,
    angebote,
    sondertage,
    selectedSonderEvent,
    teacherStats,
  } = useSondertagContext();

  /**
   * =========================================================
   * LOGO
   * =========================================================
   */

  const loadLogo = async () => {
    const img = new Image();

    img.src = "/static/images/ksb/ksb_logo.png";

    await new Promise((resolve) => {
      img.onload = resolve;
    });

    return img;
  };

  /**
   * =========================================================
   * HEADER
   * =========================================================
   */

  const renderHeaderFactory = (doc, img, margin, pageWidth) => {
    return () => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      const targetWidth = pageWidth - margin.left - margin.right;

      const targetHeight = (imgHeight / imgWidth) * targetWidth;

      doc.addImage(
        img,
        "PNG",
        margin.left - 10,
        10,
        targetWidth + 22,
        targetHeight + 2,
      );

      const titleY = 10 + targetHeight + 6;

      doc.setFont(undefined, "bold");
      doc.setFontSize(18);

      doc.text(
        `${selectedSonderEvent?.label ?? ""} - ${selectedSchoolyear?.label ?? ""}`,
        margin.left,
        titleY,
      );

      doc.line(margin.left, titleY + 3, pageWidth - margin.right, titleY + 3);

      return titleY + 10;
    };
  };

  /**
   * =========================================================
   * Adapt Kürzel
   * =========================================================
   */

  function formatAbbr(abbr) {
    if (!abbr) return "";

    const clean = abbr.trim().toLowerCase();

    if (clean.length <= 2) {
      return clean.toUpperCase();
    }

    return (
      clean[0].toUpperCase() +
      clean.slice(1, -1) +
      clean[clean.length - 1].toUpperCase()
    );
  }

  /**
   * =========================================================
   * HELPERS
   * =========================================================
   */

  const getOwnersLabel = (angebot) => {
    if (!angebot?.owners?.length) {
      return "-";
    }

    return angebot.owners.map((owner) => owner.abbreviation).join(", ");
  };

  const sortJahrgaenge = (a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);

    return numA - numB;
  };

  /**
   * =========================================================
   * WOCHENPLAN PDF
   * =========================================================
   */

  const generatePDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const margin = {
        top: 20,
        bottom: 20,
        left: 15,
        right: 15,
      };

      const pageWidth = doc.internal.pageSize.getWidth();

      const pageHeight = doc.internal.pageSize.getHeight();

      const img = await loadLogo();

      const renderHeader = renderHeaderFactory(doc, img, margin, pageWidth);

      /**
       * =====================================================
       * BUILD DAY STRUCTURE
       * =====================================================
       */

      const data = {};

      angebote.forEach((angebot) => {
        const { sondertagId, jahrgangTitle, klasseId, halbtag } = angebot;

        if (!data[sondertagId]) {
          data[sondertagId] = {};
        }

        if (!data[sondertagId][jahrgangTitle]) {
          data[sondertagId][jahrgangTitle] = {};
        }

        if (!data[sondertagId][jahrgangTitle][klasseId]) {
          data[sondertagId][jahrgangTitle][klasseId] = {};
        }

        data[sondertagId][jahrgangTitle][klasseId][halbtag] = angebot;
      });

      /**
       * =====================================================
       * RENDER DAYS
       * =====================================================
       */

      sondertage.forEach((sondertag, pageIndex) => {
        if (pageIndex > 0) {
          doc.addPage();
        }

        let y = renderHeader();

        /**
         * DAY TITLE
         */

        doc.setFont(undefined, "bold");
        doc.setFontSize(15);

        doc.text(`${sondertag.label} (${sondertag.time})`, margin.left, y);

        y += 10;

        const jahrgaenge = data[sondertag.id] || {};

        Object.keys(jahrgaenge)
          .sort(sortJahrgaenge)
          .forEach((jahrgangTitle) => {
            const klassen = jahrgaenge[jahrgangTitle];

            /**
             * PAGE BREAK
             */

            if (y > pageHeight - 40) {
              doc.addPage();
              y = renderHeader();
            }

            /**
             * JAHRGANG
             */

            doc.setFont(undefined, "bold");
            doc.setFontSize(11);

            doc.text(jahrgangTitle, margin.left, y);

            y += 6;

            /**
             * HEADER ROW
             */

            const klasseX = 15;
            const vmX = 45;
            const nmX = 115;

            doc.setFont(undefined, "bold");
            doc.setFontSize(9);

            doc.text("Klasse", klasseX, y);
            doc.text("Vormittag", vmX, y);
            doc.text("Nachmittag", nmX, y);

            y += 2;

            doc.line(margin.left, y, pageWidth - margin.right, y);

            y += 5;

            /**
             * CLASS ROWS
             */

            Object.keys(klassen)
              .sort((a, b) =>
                a.localeCompare(b, undefined, {
                  numeric: true,
                }),
              )
              .forEach((klasseId) => {
                const slots = klassen[klasseId];

                const vm = slots.vormittag || null;

                const nm = slots.nachmittag || null;

                const vmText = vm?.title
                  ? `${vm.title} (${getOwnersLabel(vm)})`
                  : "unbesetzt";

                const nmText = nm?.title
                  ? `${nm.title} (${getOwnersLabel(nm)})`
                  : "unbesetzt";

                const vmLines = doc.splitTextToSize(vmText, 70);

                const nmLines = doc.splitTextToSize(nmText, 70);

                const maxLines = Math.max(vmLines.length, nmLines.length);

                const getDisplayClassName = (klasseId, vm, nm) => {
                  return (
                    nm?.specialTitle ||
                    vm?.specialTitle ||
                    klasseId.toUpperCase()
                  );
                };

                if (y + maxLines * 4 > pageHeight - 20) {
                  doc.addPage();
                  y = renderHeader();
                }

                doc.setFont(undefined, "bold");

                doc.text(getDisplayClassName(klasseId, vm, nm), klasseX, y);

                doc.setFont(undefined, "normal");

                const isLeerAngebot = (angebot) =>
                  angebot?.owners?.length === 1 &&
                  angebot.owners[0]?.abbreviation?.toLowerCase() === "leer";

                const vmIsRed = !vm.title; //angebot not taken
                const vmIsOrange = isLeerAngebot(vm); //angebot only reserved, not covered

                const nmIsRed = !nm.title;
                const nmIsOrange = isLeerAngebot(nm);

                // Vormittag
                if (vmIsRed) {
                  doc.setTextColor(220, 0, 0);
                } else if (vmIsOrange) {
                  doc.setTextColor(255, 140, 0);
                } else {
                  doc.setTextColor(0, 0, 0);
                }

                vmLines.forEach((line, i) => {
                  doc.text(line, vmX, y + i * 4);
                });

                // Nachmittag
                if (nmIsRed) {
                  doc.setTextColor(220, 0, 0);
                } else if (nmIsOrange) {
                  doc.setTextColor(255, 140, 0);
                } else {
                  doc.setTextColor(0, 0, 0);
                }

                nmLines.forEach((line, i) => {
                  doc.text(line, nmX, y + i * 4);
                });

                // ✅ reset for subsequent rows
                doc.setTextColor(0, 0, 0);

                y += maxLines * 4 + 2;
              });

            y += 6;
          });

        /**
         * FOOTER
         */

        doc.setFontSize(8);

        doc.text(
          `Seite ${doc.getCurrentPageInfo().pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          {
            align: "center",
          },
        );
      });

      doc.save(
        `${selectedSchoolyear?.label}_${selectedSonderEvent?.label}.pdf`,
      );
    } catch (error) {
      console.error("generatePDF error:", error);
    }
  };

  /**
   * =========================================================
   * PARTICIPANT PDF
   * =========================================================
   */

  const generateParticipantPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const margin = {
        top: 20,
        bottom: 20,
        left: 15,
        right: 15,
      };

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const img = await loadLogo();

      const renderHeader = renderHeaderFactory(doc, img, margin, pageWidth);

      /**
       * =====================================================
       * BUILD TEACHER MAP
       * =====================================================
       */

      const teacherMap = {};

      angebote.forEach((angebot) => {
        if (!angebot.owners?.length) return;

        angebot.owners.forEach((owner) => {
          if (!teacherMap[owner.abbreviation]) {
            teacherMap[owner.abbreviation] = {
              abbreviation: owner.abbreviation,
              days: {},
            };
          }

          if (!teacherMap[owner.abbreviation].days[angebot.sondertagId]) {
            teacherMap[owner.abbreviation].days[angebot.sondertagId] = {};
          }

          teacherMap[owner.abbreviation].days[angebot.sondertagId][
            angebot.halbtag
          ] = angebot;
        });
      });

      const teachers = Object.values(teacherMap)
        .filter(
          (t) => !["ubsz", "leer"].includes(t.abbreviation?.toLowerCase()),
        )
        .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

      let y = renderHeader();

      doc.setFont(undefined, "bold");
      doc.setFontSize(14);

      doc.text("Lehrpersonenübersicht", margin.left, y);

      y += 10;

      teachers.forEach((teacher) => {
        // page break
        if (y > pageHeight - 50) {
          doc.addPage();
          y = renderHeader();

          doc.setFont(undefined, "bold");
          doc.setFontSize(14);
          doc.text("Lehrpersonenübersicht", margin.left, y);
          y += 10;
        }

        /**
         * Teacher heading
         */

        doc.setFont(undefined, "bold");
        doc.setFontSize(8);

        doc.text(formatAbbr(teacher.abbreviation), margin.left, y);

        y += 5;

        /**
         * All days
         */

        sondertage.forEach((tag) => {
          const dayAssignments = teacher.days[tag.id] || {};

          const vmAngebot = dayAssignments.vormittag;
          const nmAngebot = dayAssignments.nachmittag;

          const vmIsFree = !vmAngebot?.title;
          const nmIsFree = !nmAngebot?.title;

          doc.setFont(undefined, "normal");
          doc.setFontSize(8);

          // Day
          doc.setTextColor(0, 0, 0);

          doc.text(`${tag.label} (${tag.time})`, margin.left + 2, y);

          // Vormittag
          if (vmIsFree) {
            doc.setTextColor(0, 140, 0);
          } else {
            doc.setTextColor(0, 0, 0);
          }

          doc.text(`VM: ${vmAngebot?.title ?? "frei"}`, margin.left + 50, y);

          // Nachmittag
          if (nmIsFree) {
            doc.setTextColor(0, 140, 0);
          } else {
            doc.setTextColor(0, 0, 0);
          }

          doc.text(`VM: ${nmAngebot?.title ?? "frei"}`, margin.left + 120, y);

          doc.setTextColor(0, 0, 0);

          y += 4;
        });

        y += 4;
      });

      doc.save(
        `${selectedSchoolyear?.label}_${selectedSonderEvent?.label}_Lehrpersonen.pdf`,
      );
    } catch (error) {
      console.error("generateParticipantPDF error:", error);
    }
  };

  const renderRankingHeader = (doc, img, margin, pageWidth) => {
    return () => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      const targetWidth = pageWidth - margin.left - margin.right;

      const targetHeight = (imgHeight / imgWidth) * targetWidth;

      doc.addImage(
        img,
        "PNG",
        margin.left - 10,
        10,
        targetWidth + 22,
        targetHeight + 2,
      );

      const titleY = 10 + targetHeight + 6;

      doc.setFont(undefined, "bold");
      doc.setFontSize(18);

      doc.text(
        `Arbeitsverteilung Lehrpersonen - ${selectedSchoolyear?.label ?? ""}`,
        margin.left,
        titleY,
      );

      doc.line(margin.left, titleY + 3, pageWidth - margin.right, titleY + 3);

      return titleY + 10;
    };
  };

  const generateRankingPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const margin = {
        top: 20,
        bottom: 20,
        left: 15,
        right: 15,
      };

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const img = await loadLogo();

      const renderHeader = renderRankingHeader(doc, img, margin, pageWidth);

      let y = renderHeader();

      const ranking = Object.entries(teacherStats || {})
        .map(([abbr, stats]) => ({
          abbr,
          ...stats,
        }))
        .filter((t) => t.target > 0)
        .sort((a, b) => b.target - b.actual - (a.target - a.actual));

      /**
       * Table Header
       */

      doc.setFont(undefined, "bold");
      doc.setFontSize(10);

      doc.text("LP", 15, y);
      doc.text("Ist", 50, y);
      doc.text("Soll (gem. UP", 75, y);
      doc.text("%", 105, y);

      doc.line(margin.left, y + 2, pageWidth - margin.right, y + 2);

      y += 8;

      ranking.forEach((t) => {
        if (y > pageHeight - 20) {
          doc.addPage();
          y = renderHeader();
        }

        const percent = Math.round(t.ratio * 100);

        /**
         * Color logic
         */

        let color = [0, 0, 0];

        if (percent > 100) {
          color = [0, 140, 0];
        } else if (percent < 90) {
          color = [220, 0, 0];
        }

        doc.setFont(undefined, "normal");
        doc.setFontSize(9);

        doc.setTextColor(0, 0, 0);
        doc.text(formatAbbr(t.abbr), 15, y);

        doc.text(String(t.actual), 50, y);

        doc.text(t.target.toFixed(1), 75, y);

        doc.setTextColor(...color);

        doc.text(`${percent}%`, 105, y);

        doc.setTextColor(0, 0, 0);

        y += 5;
      });

      y += 5;

      doc.line(margin.left, y, pageWidth - margin.right, y);

      y += 8;

      const suggestion = ranking[0];

      if (suggestion) {
        doc.setFont(undefined, "bold");

        doc.text(
          `Empfehlung: Nächster Einsatz - ${suggestion.abbr}`,
          margin.left,
          y,
        );
      }

      doc.save(
        `${selectedSchoolyear?.label}_${selectedSonderEvent?.label}_Ranking.pdf`,
      );
    } catch (error) {
      console.error("generateRankingPDF error:", error);
    }
  };

  return {
    generatePDF,
    generateParticipantPDF,
    generateRankingPDF,
  };
}
